import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  MessageCircle, X, Send, CheckCircle2, Loader2, ChevronLeft, LifeBuoy,
  Sparkles, Copy, ArrowRight, ClipboardList, Circle,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import useSupportTicketLive from "@/hooks/useSupportTicketLive";

/**
 * SupportBubble — floating help widget (bottom-right).
 *
 * States:
 *   • idle       — small chat bubble, gentle pulse.
 *   • form       — ticket submission form (category / subject / message).
 *   • success    — shows the new ticket ID + copy-link + open-view CTA.
 *   • history    — list of previously-submitted tickets from localStorage.
 *
 * Ticket IDs + view tokens are cached in localStorage so a visitor can come
 * back to their tickets from any page. Works anonymously — no login needed.
 */

const LS_KEY = "mysticast_support_tickets";  // { tickets: [{ id, token, subject, at }] }

function readLocalTickets() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const data = JSON.parse(raw);
    return Array.isArray(data.tickets) ? data.tickets : [];
  } catch { return []; }
}

function saveLocalTicket({ id, token, subject, category }) {
  try {
    const list = readLocalTickets();
    // Dedupe by id
    const filtered = list.filter((t) => t.id !== id);
    filtered.unshift({ id, token, subject, category, at: new Date().toISOString() });
    localStorage.setItem(LS_KEY, JSON.stringify({ tickets: filtered.slice(0, 20) }));
  } catch { /* localStorage unavailable — swallow */ }
}


export default function SupportBubble() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [screen, setScreen] = useState("form"); // form | success | history | chat
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("support");
  const [email, setEmail] = useState(user?.email || "");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { id, view_token, view_url }
  const [tickets, setTickets] = useState(() => readLocalTickets());
  const [activeTicket, setActiveTicket] = useState(null); // { id, token, subject }
  const drawerRef = useRef(null);

  // Prefill email when the user logs in later
  useEffect(() => {
    if (user?.email && !email) setEmail(user.email);
  }, [user]);

  // Load categories once
  useEffect(() => {
    api.get("/support/categories")
      .then((r) => setCategories(r.data.categories || []))
      .catch(() => setCategories([
        { key: "support", label: "General Support" },
        { key: "billing", label: "Billing / Payments" },
        { key: "privacy", label: "Privacy" },
        { key: "dmca", label: "DMCA / Copyright" },
        { key: "legal", label: "Legal" },
        { key: "press", label: "Press / Business" },
        { key: "safety", label: "Trust & Safety" },
        { key: "bug", label: "Bug Report" },
      ]));
  }, []);

  // Refresh local tickets whenever the drawer opens
  useEffect(() => {
    if (open) setTickets(readLocalTickets());
  }, [open]);

  // Escape to close
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && open) setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const reset = () => {
    setSubject("");
    setMessage("");
    setCategory("support");
    setResult(null);
    setScreen("form");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!email || !subject.trim() || message.trim().length < 10) {
      toast.error("Please fill in email, subject and a message of at least 10 characters.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/support/tickets", {
        email: email.trim(),
        category,
        subject: subject.trim(),
        message: message.trim(),
      });
      const data = r.data;
      saveLocalTicket({ id: data.id, token: data.view_token, subject: subject.trim(), category });
      setResult(data);
      // Land the user directly in the live chat conversation instead of a
      // static "ticket created" confirmation.
      setActiveTicket({ id: data.id, token: data.view_token, subject: subject.trim() });
      setScreen("chat");
      toast.success("Ticket opened — you can chat with us live.");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        toast.error("Too many tickets from your location. Please wait an hour or email us directly.");
      } else {
        toast.error(err?.response?.data?.detail || "Could not submit ticket. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const openTicket = (t) => {
    setOpen(false);
    nav(`/support/ticket/${t.id}?token=${encodeURIComponent(t.token)}`);
  };

  const copyLink = async () => {
    if (!result) return;
    const url = `${window.location.origin}${result.view_url}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Ticket link copied.");
    } catch { toast.error("Could not copy — please copy manually."); }
  };

  return (
    <>
      {/* Floating bubble — always visible bottom-right */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="support-bubble-open"
          aria-label="Open support"
          className="fixed z-[80] bottom-5 right-5 sm:bottom-6 sm:right-6 group"
        >
          <span
            className="absolute inset-0 rounded-full animate-ping opacity-40"
            style={{ background: "var(--sc-cyan)" }}
          />
          <span
            className="relative flex items-center justify-center w-14 h-14 rounded-full border-2 shadow-2xl transition-transform group-hover:scale-105"
            style={{
              background: "linear-gradient(135deg, #0F0F14 0%, #1a1a24 100%)",
              borderColor: "var(--sc-cyan)",
              boxShadow: "0 0 24px rgba(0,240,255,0.4)",
            }}
          >
            <MessageCircle className="w-6 h-6" style={{ color: "var(--sc-cyan)" }} />
            {tickets.length > 0 && (
              <span
                className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center sc-mono"
                style={{ background: "var(--sc-pink)", color: "#0A0A0C" }}
                data-testid="support-bubble-count"
              >
                {tickets.length}
              </span>
            )}
          </span>
        </button>
      )}

      {/* Drawer — slides in from bottom-right (mobile: full-width bottom sheet) */}
      {open && (
        <>
          <div
            className="fixed inset-0 z-[79] bg-black/40 backdrop-blur-sm sm:hidden"
            onClick={() => setOpen(false)}
          />
          <div
            ref={drawerRef}
            data-testid="support-bubble-drawer"
            className="fixed z-[80] bottom-0 left-0 sm:bottom-20 sm:left-auto sm:right-5 w-full sm:w-[400px] max-w-[calc(100vw-1.5rem)] max-h-[90vh] sm:max-h-[640px] flex flex-col rounded-t-2xl sm:rounded-lg overflow-hidden border-2 shadow-2xl"
            style={{
              background: "rgba(10,10,12,0.98)",
              backdropFilter: "blur(20px)",
              borderColor: "rgba(0,240,255,0.4)",
              boxShadow: "0 0 40px rgba(0,240,255,0.25)",
            }}
          >
            {/* Header */}
            <div
              className="flex items-center justify-between px-4 py-3 border-b flex-shrink-0"
              style={{ borderColor: "rgba(255,255,255,0.08)" }}
            >
              <div className="flex items-center gap-2">
                <LifeBuoy className="w-4 h-4" style={{ color: "var(--sc-cyan)" }} />
                <span className="sc-heading text-sm font-bold">MystiCast Support</span>
                <span
                  className="chip-neon volt text-[9px]"
                  style={{ padding: "1px 6px" }}
                >
                  ONLINE
                </span>
              </div>
              <div className="flex items-center gap-1">
                {screen !== "form" && (
                  <button
                    type="button"
                    onClick={() => { reset(); setScreen("form"); }}
                    className="p-1.5 rounded hover:bg-white/10 transition"
                    aria-label="New ticket"
                    data-testid="support-new-btn"
                  >
                    <ChevronLeft className="w-4 h-4 text-zinc-400" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="p-1.5 rounded hover:bg-white/10 transition"
                  aria-label="Close"
                  data-testid="support-bubble-close"
                >
                  <X className="w-4 h-4 text-zinc-400" />
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div
              className="flex text-[11px] sc-mono border-b flex-shrink-0"
              style={{ borderColor: "rgba(255,255,255,0.08)" }}
            >
              <button
                type="button"
                onClick={() => setScreen("form")}
                className={`flex-1 py-2 px-3 transition ${screen === "form" ? "text-white border-b-2" : "text-zinc-500 hover:text-zinc-300"}`}
                style={screen === "form" ? { borderColor: "var(--sc-cyan)", color: "var(--sc-cyan)" } : {}}
                data-testid="support-tab-new"
              >
                NEW TICKET
              </button>
              <button
                type="button"
                onClick={() => setScreen("history")}
                className={`flex-1 py-2 px-3 transition flex items-center justify-center gap-1.5 ${screen === "history" ? "text-white border-b-2" : "text-zinc-500 hover:text-zinc-300"}`}
                style={screen === "history" ? { borderColor: "var(--sc-cyan)", color: "var(--sc-cyan)" } : {}}
                data-testid="support-tab-history"
              >
                <ClipboardList className="w-3 h-3" /> MY TICKETS
                {tickets.length > 0 && (
                  <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded" style={{ background: "rgba(0,240,255,0.15)" }}>
                    {tickets.length}
                  </span>
                )}
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-4">
              {screen === "form" && (
                <form onSubmit={submit} className="space-y-3" data-testid="support-form">
                  <p className="text-xs text-zinc-400 leading-relaxed mb-2">
                    Question, bug, or feedback? Fill this out — a real human replies within 1 business day.
                    You&apos;ll get a link to check your ticket status.
                  </p>

                  <div>
                    <label className="sc-label text-[10px] block mb-1.5" style={{ color: "var(--sc-cyan)" }}>
                      CATEGORY
                    </label>
                    <select
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                      data-testid="support-category"
                      className="w-full px-3 py-2 bg-[#0F0F14] border border-white/10 focus:border-[color:var(--sc-cyan)] focus:outline-none rounded sc-mono text-sm text-zinc-100"
                    >
                      {categories.map((c) => (
                        <option key={c.key} value={c.key}>{c.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="sc-label text-[10px] block mb-1.5" style={{ color: "var(--sc-cyan)" }}>
                      EMAIL {user && <span className="text-zinc-600 normal-case ml-1">(pre-filled)</span>}
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      autoComplete="email"
                      data-testid="support-email"
                      placeholder="you@example.com"
                      className="w-full px-3 py-2 bg-[#0F0F14] border border-white/10 focus:border-[color:var(--sc-cyan)] focus:outline-none rounded sc-mono text-sm text-zinc-100 placeholder:text-zinc-600"
                    />
                  </div>

                  <div>
                    <label className="sc-label text-[10px] block mb-1.5" style={{ color: "var(--sc-cyan)" }}>
                      SUBJECT
                    </label>
                    <input
                      type="text"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      required
                      minLength={3}
                      maxLength={140}
                      data-testid="support-subject"
                      placeholder="One-line summary of your issue"
                      className="w-full px-3 py-2 bg-[#0F0F14] border border-white/10 focus:border-[color:var(--sc-cyan)] focus:outline-none rounded sc-mono text-sm text-zinc-100 placeholder:text-zinc-600"
                    />
                  </div>

                  <div>
                    <label className="sc-label text-[10px] block mb-1.5" style={{ color: "var(--sc-cyan)" }}>
                      MESSAGE
                    </label>
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      required
                      minLength={10}
                      maxLength={4000}
                      rows={5}
                      data-testid="support-message"
                      placeholder="Describe what happened. Include any relevant details — transaction IDs, streamer names, URLs, screenshots via link, etc."
                      className="w-full px-3 py-2 bg-[#0F0F14] border border-white/10 focus:border-[color:var(--sc-cyan)] focus:outline-none rounded sc-mono text-xs text-zinc-100 placeholder:text-zinc-600 resize-none"
                    />
                    <div className="text-[10px] text-zinc-600 mt-1 flex justify-between">
                      <span>{message.length}/4000</span>
                      <span>min. 10 characters</span>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="support-submit"
                    className="w-full sc-btn text-sm flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    {submitting ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</>
                    ) : (
                      <><Send className="w-4 h-4" /> Open Ticket</>
                    )}
                  </button>
                </form>
              )}

              {screen === "success" && result && (
                <div className="space-y-4 text-center py-4" data-testid="support-success">
                  <div
                    className="w-14 h-14 mx-auto rounded-full flex items-center justify-center border-2"
                    style={{ borderColor: "var(--sc-volt)", background: "rgba(204,255,0,0.05)" }}
                  >
                    <CheckCircle2 className="w-7 h-7" style={{ color: "var(--sc-volt)" }} />
                  </div>
                  <div>
                    <div className="sc-heading text-lg font-black">Ticket opened.</div>
                    <div className="text-xs text-zinc-400 mt-1 leading-relaxed">
                      We&apos;ll reply to <span className="text-white">{email}</span> and inside your ticket thread.
                    </div>
                  </div>
                  <div className="term-card cyan p-3 text-left space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="sc-label text-[9px]" style={{ color: "var(--sc-cyan)" }}>TICKET ID</span>
                      <button
                        type="button"
                        onClick={copyLink}
                        className="text-[10px] text-zinc-400 hover:text-white flex items-center gap-1"
                      >
                        <Copy className="w-3 h-3" /> COPY LINK
                      </button>
                    </div>
                    <div className="sc-mono text-xs text-zinc-200 break-all">
                      #{result.id.slice(0, 8).toUpperCase()}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => openTicket({ id: result.id, token: result.view_token })}
                    className="w-full sc-btn text-sm flex items-center justify-center gap-2"
                    data-testid="support-view-btn"
                  >
                    View Ticket <ArrowRight className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={reset}
                    className="w-full sc-btn-ghost text-xs"
                  >
                    Open another
                  </button>
                </div>
              )}

              {screen === "chat" && activeTicket && (
                <BubbleLiveChat
                  ticket={activeTicket}
                  onBack={() => { setScreen("history"); setTickets(readLocalTickets()); }}
                />
              )}


              {screen === "history" && (
                <div className="space-y-2" data-testid="support-history">
                  {tickets.length === 0 ? (
                    <div className="text-center py-10">
                      <Sparkles className="w-6 h-6 mx-auto mb-2 text-zinc-600" />
                      <div className="sc-heading text-sm font-bold text-zinc-400">No tickets yet</div>
                      <div className="text-xs text-zinc-600 mt-1">
                        Tickets you open on this browser will appear here.
                      </div>
                      <button
                        type="button"
                        onClick={() => setScreen("form")}
                        className="mt-4 sc-btn text-xs inline-flex items-center gap-1.5"
                      >
                        <Send className="w-3.5 h-3.5" /> Open your first ticket
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="sc-label text-[10px] mb-1" style={{ color: "var(--sc-cyan)" }}>
                        RECENT TICKETS
                      </div>
                      {tickets.map((t) => (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => openTicket(t)}
                          className="w-full text-left term-card cyan p-3 hover:brightness-125 transition"
                          data-testid={`support-history-${t.id}`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="sc-heading text-xs font-bold truncate">{t.subject || "Untitled"}</div>
                            <span
                              className="sc-mono text-[9px] px-1.5 py-0.5 rounded flex-shrink-0"
                              style={{ background: "rgba(0,240,255,0.1)", color: "var(--sc-cyan)" }}
                            >
                              #{t.id.slice(0, 6).toUpperCase()}
                            </span>
                          </div>
                          <div className="text-[10px] text-zinc-500 mt-1 sc-mono">
                            {t.category?.toUpperCase() || "SUPPORT"} · {new Date(t.at).toLocaleDateString()}
                          </div>
                        </button>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Footer — direct email fallback */}
            <div
              className="px-4 py-2 border-t flex-shrink-0 sc-mono text-[10px] text-zinc-600"
              style={{ borderColor: "rgba(255,255,255,0.06)" }}
            >
              Prefer email? <a href="mailto:support@mysticast.tv" className="text-zinc-400 hover:text-white underline">support@mysticast.tv</a>
            </div>
          </div>
        </>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// BubbleLiveChat — inline chat conversation rendered inside the support popup.
// Uses the same WebSocket layer as the staff console for real-time replies.
// ---------------------------------------------------------------------------
function BubbleLiveChat({ ticket, onBack }) {
  const [thread, setThread] = useState([]);
  const [status, setStatus] = useState("open");
  const [claimedBy, setClaimedBy] = useState(null);
  const [staffTyping, setStaffTyping] = useState(false);
  const [connState, setConnState] = useState("connecting");
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const wsRef = useRef(null);
  const scrollRef = useRef(null);
  const typingTimer = useRef(null);

  // Full thread refresh — used on mount, on every WS (re)connect, and by the
  // polling fallback whenever the WS isn't connected. Keeps the customer in
  // sync even after a network flip or a proxy dropping their socket.
  const syncThread = useCallback(async () => {
    try {
      const r = await api.get(`/support/tickets/${ticket.id}`, { params: { token: ticket.token } });
      setThread(r.data.messages || []);
      setStatus(r.data.status || "open");
      setClaimedBy(r.data.claimed_by || null);
    } catch {
      // Keep prior thread on transient failure — polling will retry.
    }
  }, [ticket.id, ticket.token]);

  useEffect(() => { syncThread(); }, [syncThread]);

  const wsUrl = useMemo(() => {
    const backend = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    const proto = backend.startsWith("https") ? "wss" : "ws";
    const host = backend.replace(/^https?:\/\//, "");
    return `${proto}://${host}/api/support/ws/${ticket.id}?token=${encodeURIComponent(ticket.token)}`;
  }, [ticket.id, ticket.token]);

  const { send: sendWs } = useSupportTicketLive({
    enabled: true,
    wsUrl,
    onStateChange: setConnState,
    onSync: syncThread,
    onMessage: useCallback((message) => setThread((t) => [...t, message]), []),
    onPresence: useCallback(() => {}, []),
    onTyping: useCallback((p) => { if (p.who === "staff") setStaffTyping(!!p.is_typing); }, []),
    onClaim: useCallback((p) => setClaimedBy(p.claimed_by), []),
  });
  useEffect(() => { wsRef.current = { send: sendWs }; }, [sendWs]);

  // Autoscroll.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [thread.length, staffTyping]);

  const sendTyping = (isTyping) => {
    if (!wsRef.current?.send) return;
    wsRef.current.send({ type: "typing", is_typing: isTyping });
  };
  const onChange = (v) => {
    setReply(v);
    sendTyping(true);
    if (typingTimer.current) clearTimeout(typingTimer.current);
    typingTimer.current = setTimeout(() => sendTyping(false), 1500);
  };

  const send = async (e) => {
    e?.preventDefault?.();
    if (!reply.trim() || sending) return;
    setSending(true);
    try {
      await api.post(`/support/tickets/${ticket.id}/reply`, { message: reply.trim() }, {
        params: { token: ticket.token },
      });
      setReply("");
      sendTyping(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send reply");
    } finally { setSending(false); }
  };

  return (
    <div className="flex flex-col h-[420px]" data-testid="support-bubble-chat">
      {/* Chat header */}
      <div className="pb-2 mb-2 border-b border-white/5">
        <div className="flex items-center gap-2">
          <button onClick={onBack} className="p-1 rounded hover:bg-white/5 text-zinc-500 hover:text-white" data-testid="support-chat-back">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="min-w-0 flex-1">
            <div className="sc-heading text-sm font-bold truncate">{ticket.subject || "Support ticket"}</div>
            <div className="text-[10px] sc-mono text-zinc-500 flex items-center gap-1.5">
              <Circle className={`w-2 h-2 fill-current ${connState === "connected" ? "text-[color:var(--sc-volt)]" : "text-zinc-700"}`} />
              <span>{connState === "connected" ? "live" : connState}</span>
              <span>·</span>
              <span>#{ticket.id.slice(0, 6).toUpperCase()}</span>
              {claimedBy && <><span>·</span><span className="text-[color:var(--sc-cyan)]">@{claimedBy} on it</span></>}
            </div>
          </div>
        </div>
      </div>

      {/* Thread */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-2 pr-1">
        {thread.map((m) => {
          const isStaff = m.from === "staff";
          return (
            <div key={m.id} className={`flex ${isStaff ? "justify-start" : "justify-end"}`} data-testid={`bubble-msg-${m.id}`}>
              <div className={`max-w-[85%] p-2 rounded-md border text-xs ${
                isStaff ? "border-[color:var(--sc-cyan)]/30 bg-[rgba(0,240,255,0.08)]"
                        : "border-white/10 bg-white/[0.05]"}`}>
                <div className="flex items-center gap-1.5 mb-0.5">
                  <span className="sc-mono text-[8px] font-bold" style={{ color: isStaff ? "var(--sc-cyan)" : "#a1a1aa" }}>
                    {isStaff ? "STAFF" : "YOU"}
                  </span>
                  <span className="sc-mono text-[9px] text-zinc-600">{new Date(m.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                </div>
                <div className="text-zinc-100 whitespace-pre-wrap leading-relaxed">{m.body}</div>
              </div>
            </div>
          );
        })}
        {staffTyping && (
          <div className="flex justify-start" data-testid="bubble-staff-typing">
            <div className="px-2 py-1 rounded-md border border-white/10 bg-white/[0.02] flex items-center gap-1.5">
              <span className="text-[9px] sc-mono text-zinc-500">Staff is typing</span>
              <span className="flex gap-0.5">
                <span className="w-1 h-1 rounded-full bg-zinc-500 animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-1 h-1 rounded-full bg-zinc-500 animate-bounce" style={{ animationDelay: "120ms" }} />
                <span className="w-1 h-1 rounded-full bg-zinc-500 animate-bounce" style={{ animationDelay: "240ms" }} />
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Compose */}
      {status !== "closed" ? (
        <form onSubmit={send} className="mt-2 pt-2 border-t border-white/5 flex items-end gap-2" data-testid="support-chat-form">
          <textarea
            value={reply}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(e); } }}
            placeholder="Type a message…"
            rows={1}
            maxLength={4000}
            className="flex-1 px-2 py-1.5 bg-[#0F0F14] border border-white/10 focus:border-[color:var(--sc-cyan)] focus:outline-none rounded text-xs text-zinc-100 placeholder:text-zinc-600 resize-none"
            data-testid="support-chat-input"
          />
          <button type="submit" disabled={sending || reply.trim().length < 1}
            className="sc-btn text-xs flex items-center gap-1 disabled:opacity-60 h-[34px]"
            data-testid="support-chat-send">
            {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
          </button>
        </form>
      ) : (
        <div className="mt-2 pt-2 border-t border-white/5 text-center text-[10px] text-zinc-500">
          Ticket closed. Open a new one from the ticket list.
        </div>
      )}
    </div>
  );
}

